import { getString } from "common/ui_string_labels/ui-strings-utils";

/**
 * Regex pattern that only allows letters and commas (no numbers, special characters, emails)
 */
const LETTERS_ONLY_PATTERN = /^[a-zA-Z]+$/;

/**
 * ui-strings.json paths
 */
const UI_STRING_ERROR_PATH = "common.component.LocalItOrderConfirmationContent.orderForm.errors";
const UI_STRING_EMPTY_WATCHER_ERROR_ID = "emptyWatcher";
const UI_STRING_NON_VALID_WATCHER_ERROR_ID = "nonValidWatcher";
const UI_STRING_DUPLICATE_WATCHER_ERROR_ID = "duplicateWatcher";

/**
 * Sanitizes a watcher string by trimming whitespace
 * 
 * @param {string} watcher - The watcher string to sanitize
 * @returns {string} - The trimmed watcher string
 */
export const sanitizeWatcher = (watcher: string): string => {
    return watcher.trim();
};

/**
 * Validates that a watcher string contains only letters
 * 
 * @param {string} watcher - The watcher string to validate
 * @returns {boolean} - True if watcher contains only letters, false otherwise
 */
export const validateWatcher = (watcher: string): boolean => {
    return LETTERS_ONLY_PATTERN.test(watcher);
};

/**
 * Sanitizes and filters an array of watchers
 * - Trims each watcher string
 * - Removes empty strings after trimming
 * - Returns only non-empty trimmed watchers
 * 
 * @param {string[]} watchers - Array of watcher strings to process
 * @returns {string[]} - Array of sanitized, non-empty watchers
 */
export const sanitizeAndFilterWatchers = (watchers: string[]): string[] => {
    return watchers
        .map(watcher => sanitizeWatcher(watcher))
        .filter(watcher => watcher.length > 0);
};

/**
 * Validates all watchers for form submission
 * Checks that all watchers contain only letters
 * 
 * @param {string[]} watchers - Array of watcher strings to validate
 * @returns {object} - Object containing validation status and list of invalid watchers
 */
export const validateWatchersForSubmission = (watchers: string[]): {
    isValid: boolean;
    invalidWatchers: string[]
} => {
    const sanitizedWatchers = sanitizeAndFilterWatchers(watchers);
    const invalidWatchers: string[] = [];

    for (const watcher of sanitizedWatchers) {
        if (!validateWatcher(watcher)) {
            invalidWatchers.push(watcher);
        }
    }

    return {
        isValid: invalidWatchers.length === 0,
        invalidWatchers
    };
};


/**
 * Validates a watcher input and returns an error message if invalid
 *
 * @param {string} watcher - The sanitized watcher string to validate
 * @param {string[]} currentWatchers - The current list of watchers to check for duplicates
 * @returns {string | undefined} - Error message if invalid, undefined if valid
 */
export const validateWatcherInput = (watcher: string, currentWatchers: string[]): string | undefined => {
    // Don't add empty strings after sanitization
    if (watcher.length === 0) {
        return getString(UI_STRING_EMPTY_WATCHER_ERROR_ID, UI_STRING_ERROR_PATH);
    }

    // Validate that watcher contains only letters
    if (!validateWatcher(watcher)) {
        return getString(UI_STRING_NON_VALID_WATCHER_ERROR_ID, UI_STRING_ERROR_PATH, {
            invalidWatchers: watcher,
        });
    }

    // Check for duplicates (case-insensitive)
    const lowerCaseWatcher = watcher.toLowerCase();
    if (currentWatchers.some((existing) => existing.toLowerCase() === lowerCaseWatcher)) {
        return getString(UI_STRING_DUPLICATE_WATCHER_ERROR_ID, UI_STRING_ERROR_PATH, {
            duplicateWatcher: watcher,
        });
    }

    return undefined;
};